import { cloneDeep, isEmpty, isEqual } from 'lodash';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useFeesBySolutionContext, VariableSelector } from 'sonata-platform-components';
import { normalizeIABVariables } from 'sonata-static-storage/lib/helpers/variables.helpers';
import { IABCategories } from 'sonata-static-storage/lib/samples/iabCategories.samples';
import { FeeSolutionCapabilityCategory } from 'sonata-static-storage/lib/schemas/feeSolution/feeSolutionCapabilityCategory.schema';

import { CapabilitiesInvoice } from '../../CapabilitiesInvoice';
import TotalInvoice from '../../TotalInvoice/TotalInvoice';
import { StepThreeStyled } from './stepThree.styled';

type State = {
  selectedCapabilities: string[];
};

export type StepWizardCategories = {
  categoryId: string;
  categoryName: string;
  subcategories?: StepWizardCategories[];
};

const StepThree: React.FC = () => {
  const { formatMessage } = useIntl();

  const {
    campaign,
    capabilities,
    capabilitiesByUser,
    capabilityCategories,
    currencyInfo,
    getStep3,
    handleStep3Change,
    initialCampaign,
    mode,
    plans,
    solutions
  } = useFeesBySolutionContext();
  const { extraCapabilities = [], selectedPlan } = getStep3();

  const isSimplifiedSolution = isEmpty(extraCapabilities);

  const feeSolution = plans?.find(
    (item: { _id: string; feeSolution: string }) => item._id === selectedPlan?.plan
  )?.feeSolution;
  const selectedSolution = solutions?.find((item: { _id: string }) => item._id === feeSolution);

  const [{ selectedCapabilities }, setState] = useState<State>({
    selectedCapabilities: extraCapabilities
  });

  const getFormatCapabilities = () => {
    const formatCapabilities: StepWizardCategories[] = [];
    const plan = plans?.find(
      (item: { _id: string; extraCapabilities: string[]; feeSolution: string }) => item._id === selectedPlan?.plan
    );
    capabilities
      ?.filter(
        ({ _id }: { _id: string }) =>
          plan!.extraCapabilities.includes(_id!) &&
          capabilitiesByUser?.some(
            (item: { feeSolution: string; capability: string }) =>
              item.feeSolution === plan?.feeSolution && item.capability === _id
          )
      )
      .forEach((capability: { _id: string; copy: string; category: string }) => {
        const capabilityCategory: FeeSolutionCapabilityCategory | undefined = capabilityCategories?.find(
          (item: { _id: string }) => item._id === capability.category
        );
        if (capabilityCategory) {
          const formatCapability = formatCapabilities.find(item => item.categoryId === capability.category);
          const newItem = {
            categoryId: capability._id!,
            categoryName: capability.copy
          };
          if (formatCapability) {
            formatCapability.subcategories?.push(newItem);
          } else {
            const newFormatCapability: IABCategories = {
              categoryId: capabilityCategory._id!,
              categoryName: capabilityCategory.copy,
              subcategories: [newItem]
            };
            formatCapabilities.push(newFormatCapability);
          }
        }
      });

    return formatCapabilities;
  };

  const formatCapabilities = getFormatCapabilities();
  const { levels } = normalizeIABVariables(formatCapabilities);

  const handleChange = ({ items, maxCost }: { items?: string[]; maxCost?: number | null }) => {
    if (items && !isEqual(items, selectedCapabilities)) {
      setState(prev => ({
        ...prev,
        selectedCapabilities: items
      }));
    }
    handleStep3Change({
      extraCapabilities: items ?? selectedCapabilities,
      maxCost: maxCost !== undefined ? maxCost : null
    });
  };

  const getAlwaysSelectedItems = () => {
    const allLevels = Object.values(levels).map(item => item);
    let allItems: any[] = [];
    allLevels.forEach(item => {
      allItems = [...allItems, ...item];
    });

    const allExtraCapabilities: string[] = cloneDeep(initialCampaign.solutionsFees.extraCapabilities);
    initialCampaign.solutionsFees.extraCapabilities.forEach((extraCapability: string) => {
      const item = allItems.find(val => val.id === extraCapability);
      let parentId = item.parent ? item.parent.id : null;
      while (parentId != null) {
        allExtraCapabilities.push(parentId);
        const subItem = allItems.find(subVal => subVal.id === parentId);
        parentId = subItem.parent ? subItem.parent.id : null;
      }
    });
    return allExtraCapabilities;
  };

  const MainComponent = () => {
    if (isSimplifiedSolution) {
      return (
        <TotalInvoice
          currencyInfo={currencyInfo}
          defaultMaxCost={campaign.solutionsFees?.maxCost}
          onChange={(cost: number | null) => handleChange({ maxCost: cost })}
          selectedSolution={selectedSolution}
        />
      );
    }
    if (!isEmpty(levels)) {
      return (
        <>
          <div className="feesBySolutionStepThree__variables">
            <VariableSelector
              alwaysSelectedItems={mode === 'edit' ? getAlwaysSelectedItems() : undefined}
              idsSelectedItems={selectedCapabilities}
              items={levels}
              levelToExclude="level2"
              onChange={(items: string[]) => handleChange({ items })}
              options={[
                {
                  id: 'level0',
                  label: formatMessage({
                    defaultMessage: 'Categories',
                    id: 'feesBySolution.stepThree.categories'
                  })
                },
                {
                  id: 'level1',
                  label: formatMessage({
                    defaultMessage: 'Capabilities',
                    id: 'feesBySolution.stepThree.capabilities'
                  })
                }
              ]}
              padding="0"
            />
          </div>
          <div className="feesBySolutionStepThree__invoice">
            <CapabilitiesInvoice
              currencyInfo={currencyInfo}
              id="capabilitiesInvoice"
              selectedSolution={selectedSolution}
            />
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <StepThreeStyled className="feesBySolutionStepThree" data-testid="feesBySolutionStepThree">
      {MainComponent()}
    </StepThreeStyled>
  );
};

export default StepThree;
